import * as THREE from 'three';
import { isPointInRegion, type Region } from '../utils/geometry';

export interface HeightFieldConfig {
  /** World-space size of the field along X. */
  width: number;
  /** World-space size of the field along Z. */
  depth: number;
  /** World-space center of the field. */
  centerX?: number;
  centerZ?: number;
  /** Grid resolution — (resolution+1) x (resolution+1) sample points. */
  resolution: number;
  /** Optional initial height generator, baked into the grid once at construction. */
  generator?: (worldX: number, worldZ: number) => number;
}

/**
 * A generic, sculptable heightmap grid. This has no idea what a golf course
 * is — Terrain owns one of these and samples/mutates it; course-specific
 * shaping (flattening a green, recessing a bunker) is done by rendering-
 * layer code calling the mutation methods below, not by this class knowing
 * about "greens" or "bunkers".
 *
 * Data is the source of truth; Terrain's mesh geometry is a derived cache
 * of it (see Terrain.refreshGeometry). That split is what makes "future
 * sculpting" tractable — mutate the grid, refresh the mesh, done.
 */
export class HeightField {
  readonly width: number;
  readonly depth: number;
  readonly centerX: number;
  readonly centerZ: number;
  readonly resolution: number;

  private readonly data: Float32Array;
  private readonly cols: number;
  private readonly rows: number;

  constructor(config: HeightFieldConfig) {
    this.width = config.width;
    this.depth = config.depth;
    this.centerX = config.centerX ?? 0;
    this.centerZ = config.centerZ ?? 0;
    this.resolution = config.resolution;

    this.cols = config.resolution + 1;
    this.rows = config.resolution + 1;
    this.data = new Float32Array(this.cols * this.rows);

    if (config.generator) {
      for (let iz = 0; iz < this.rows; iz++) {
        for (let ix = 0; ix < this.cols; ix++) {
          const { x, z } = this.indexToWorld(ix, iz);
          this.data[iz * this.cols + ix] = config.generator(x, z);
        }
      }
    }
  }

  get minX(): number {
    return this.centerX - this.width / 2;
  }
  get minZ(): number {
    return this.centerZ - this.depth / 2;
  }

  indexToWorld(ix: number, iz: number): { x: number; z: number } {
    return {
      x: this.minX + (ix / this.resolution) * this.width,
      z: this.minZ + (iz / this.resolution) * this.depth,
    };
  }

  worldToIndex(worldX: number, worldZ: number): { fx: number; fz: number } {
    const fx = ((worldX - this.minX) / this.width) * this.resolution;
    const fz = ((worldZ - this.minZ) / this.depth) * this.resolution;
    return { fx, fz };
  }

  private at(ix: number, iz: number): number {
    const cix = Math.min(this.cols - 1, Math.max(0, ix));
    const ciz = Math.min(this.rows - 1, Math.max(0, iz));
    return this.data[ciz * this.cols + cix];
  }

  private setAt(ix: number, iz: number, value: number): void {
    if (ix < 0 || iz < 0 || ix >= this.cols || iz >= this.rows) return;
    this.data[iz * this.cols + ix] = value;
  }

  /** Bilinear-sampled height at any world (x, z), including outside the exact grid points. */
  sampleHeight(worldX: number, worldZ: number): number {
    const { fx, fz } = this.worldToIndex(worldX, worldZ);
    const ix0 = Math.floor(fx);
    const iz0 = Math.floor(fz);
    const tx = fx - ix0;
    const tz = fz - iz0;

    const h00 = this.at(ix0, iz0);
    const h10 = this.at(ix0 + 1, iz0);
    const h01 = this.at(ix0, iz0 + 1);
    const h11 = this.at(ix0 + 1, iz0 + 1);

    const hx0 = h00 + (h10 - h00) * tx;
    const hx1 = h01 + (h11 - h01) * tx;
    return hx0 + (hx1 - hx0) * tz;
  }

  /** Finite-difference normal at any world (x, z). */
  sampleNormal(worldX: number, worldZ: number): THREE.Vector3 {
    const step = Math.max(this.width / this.resolution, this.depth / this.resolution) * 0.5;
    const hL = this.sampleHeight(worldX - step, worldZ);
    const hR = this.sampleHeight(worldX + step, worldZ);
    const hD = this.sampleHeight(worldX, worldZ - step);
    const hU = this.sampleHeight(worldX, worldZ + step);
    const normal = new THREE.Vector3(hL - hR, 2 * step, hD - hU);
    return normal.normalize();
  }

  /** Direct grid-point access, for geometry building (Terrain reads this to build/refresh its mesh). */
  get gridData(): Float32Array {
    return this.data;
  }
  get gridCols(): number {
    return this.cols;
  }
  get gridRows(): number {
    return this.rows;
  }

  // ---- Sculpting operations ----
  // These mutate the grid directly. Callers (rendering-layer code) are
  // responsible for calling Terrain.refreshGeometry() afterward to sync
  // the visual mesh — HeightField itself has no mesh to update.

  /** Raises/lowers height within a region by `amount`, feathered over `feather` world units at the boundary. */
  applyRegionDelta(region: Region, amount: number, feather = 4): void {
    this.forEachGridPointNear(region, feather, (ix, iz, weight) => {
      this.setAt(ix, iz, this.at(ix, iz) + amount * weight);
    });
  }

  /** Blends height within a region toward `targetHeight`, feathered over `feather` world units at the boundary. */
  flattenRegionToward(region: Region, targetHeight: number, feather = 4): void {
    this.forEachGridPointNear(region, feather, (ix, iz, weight) => {
      const current = this.at(ix, iz);
      this.setAt(ix, iz, current + (targetHeight - current) * weight);
    });
  }

  /** Average height of grid points strictly inside a region — useful for computing a flattening target. */
  averageHeightInRegion(region: Region): number {
    let total = 0;
    let count = 0;
    for (let iz = 0; iz < this.rows; iz++) {
      for (let ix = 0; ix < this.cols; ix++) {
        const { x, z } = this.indexToWorld(ix, iz);
        if (isPointInRegion({ x, z }, region)) {
          total += this.at(ix, iz);
          count++;
        }
      }
    }
    return count > 0 ? total / count : 0;
  }

  /**
   * Visits every grid point inside `region`, plus a feathered ring of
   * `featherWorldUnits` outside it, calling back with a 0..1 weight (1
   * inside the region, fading to 0 at the outer edge of the feather ring).
   * This is the shared brush-falloff logic for both sculpting methods.
   */
  private forEachGridPointNear(
    region: Region,
    featherWorldUnits: number,
    callback: (ix: number, iz: number, weight: number) => void,
  ): void {
    for (let iz = 0; iz < this.rows; iz++) {
      for (let ix = 0; ix < this.cols; ix++) {
        const { x, z } = this.indexToWorld(ix, iz);
        const point = { x, z };
        if (isPointInRegion(point, region)) {
          callback(ix, iz, 1);
          continue;
        }
        if (featherWorldUnits <= 0) continue;

        const distance = distanceToRegionBoundary(point, region);
        if (distance <= featherWorldUnits) {
          const weight = 1 - distance / featherWorldUnits;
          callback(ix, iz, weight);
        }
      }
    }
  }
}

/** Approximate distance from a point to a region's boundary (exact for circles, nearest-vertex approximation for polygons). */
function distanceToRegionBoundary(point: { x: number; z: number }, region: Region): number {
  if (region.kind === 'circle') {
    const d = Math.hypot(point.x - region.center.x, point.z - region.center.z);
    return Math.abs(d - region.radius);
  }
  let closest = Infinity;
  for (const vertex of region.points) {
    const d = Math.hypot(point.x - vertex.x, point.z - vertex.z);
    if (d < closest) closest = d;
  }
  return closest;
}
